import TemplatePreview from "./TemplatePreview";
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Input,
  FormControl,
  FormHelperText,
  Container,
  Paper,
  Divider,
  IconButton,
  Switch,
  FormControlLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "./context/UserContext";
import Select from 'react-select';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const ALL_CURRENCIES = [
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
];

const formatDateDDMMYYYY = (isoDateString) => {
  const date = new Date(isoDateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const InvoiceTemplates = () => {
  const navigate = useNavigate();
  const context = useUserContext();

  const {
    userProfile,
    isAdmin,
    adminSelectedTemplate,
    setAdminSelectedTemplate,
    invoiceLogo,
    setInvoiceLogo,
    invoiceBanner,
    setInvoiceBanner,
    invoiceBusinessName,
    setInvoiceBusinessName,
    invoiceBusinessAddress,
    setInvoiceBusinessAddress,
    invoiceCurrency,
    setInvoiceCurrency,
    invoiceDefaultAdditionalNotes,
    setInvoiceDefaultAdditionalNotes,
    invoiceDefaultTermsAndConditions,
    setInvoiceDefaultTermsAndConditions,
    invoiceFooter,
    setInvoiceFooter,
    invoiceDefaultAuthorizedSignature,
    setInvoiceDefaultAuthorizedSignature,
    invoiceDefaultAuthorizedSignatureName,
    setInvoiceDefaultAuthorizedSignatureName,
    invoiceIdPrefix, setInvoiceIdPrefix,
    invoicePrefixSetAt, setInvoicePrefixSetAt,
    invoiceCustomCounter, setInvoiceCustomCounter,
    invoiceCounterSetAt, setInvoiceCounterSetAt,
    showInvoiceGST, setShowInvoiceGST,
  } = context;

  const [selectedTemplate, setSelectedTemplate] = useState(adminSelectedTemplate);
  const [showPreview, setShowPreview] = useState(false);
  const [newPrefix, setNewPrefix] = useState(invoiceIdPrefix || '');
  const [newCustomCounter, setNewCustomCounter] = useState('');
  const [counterValue, setCounterValue] = useState(null);
  const [counterLoading, setCounterLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const currencyOptions = useMemo(() => {
    const inr = ALL_CURRENCIES.find(c => c.code === 'INR');
    const usd = ALL_CURRENCIES.find(c => c.code === 'USD');
    const others = ALL_CURRENCIES
      .filter(c => c.code !== 'INR' && c.code !== 'USD')
      .sort((a, b) => a.name.localeCompare(b.name));

    const sortedCurrencies = [inr, usd, ...others].filter(Boolean);

    return sortedCurrencies.map(c => ({
      value: c.code,
      label: `${c.name} (${c.symbol})`,
      ...c
    }));
  }, []);

  const handleCurrencyChange = (selectedOption) => {
    if (typeof setInvoiceCurrency === "function") {
      setInvoiceCurrency({
        code: selectedOption.value,
        symbol: selectedOption.symbol,
        name: selectedOption.name,
      });
    }
  };

  const handleDefaultSignatureUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPEG, PNG, or JPG files are allowed.');
      return;
    }

    if (file.size > 150 * 1024) {
      alert('Signature image size must be below 150KB. Please upload an appropriately sized file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const maxWidth = 400;
        const maxHeight = 200;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        let outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const compressedImageData = canvas.toDataURL(outputType, 0.9);
        
        if (typeof setInvoiceDefaultAuthorizedSignature === 'function') {
          setInvoiceDefaultAuthorizedSignature(compressedImageData);
        }
      };
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!isAdmin && adminSelectedTemplate) {
      navigate(`/invoice-templates/${adminSelectedTemplate}`);
    }
  }, [isAdmin, adminSelectedTemplate, navigate]);

  const handleTemplateSelection = (template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleConfirmTemplate = async () => {
    if (isAdmin) {
      try {
        // Save ALL invoice settings to Firestore if tenantId exists
        if (userProfile && userProfile.tenantId) {
          const settingsRef = doc(db, `tenants/${userProfile.tenantId}/settings`, 'templates');
          const invoiceSettings = {
            invoiceTemplate: selectedTemplate,
            invoiceLogo: invoiceLogo,
            invoiceBanner: invoiceBanner,
            invoiceBusinessName: invoiceBusinessName,
            invoiceBusinessAddress: invoiceBusinessAddress,
            invoiceFooter: invoiceFooter,
            invoiceDefaultAdditionalNotes: invoiceDefaultAdditionalNotes,
            invoiceDefaultTermsAndConditions: invoiceDefaultTermsAndConditions,
            invoiceDefaultAuthorizedSignature: invoiceDefaultAuthorizedSignature,
            invoiceDefaultAuthorizedSignatureName: invoiceDefaultAuthorizedSignatureName,
            invoiceCurrency: invoiceCurrency,
            invoiceIdPrefix: invoiceIdPrefix,
            invoiceCustomCounter: invoiceCustomCounter,
            showInvoiceGST: showInvoiceGST,
          };
          await setDoc(settingsRef, invoiceSettings, { merge: true });
          console.log(`All invoice settings saved to Firestore for tenant ${userProfile.tenantId}`);
        }
        
        // Update local state
        setAdminSelectedTemplate(selectedTemplate);
        alert(`Template '${selectedTemplate}' has been confirmed and saved!`);
        navigate(selectedTemplate);
      } catch (error) {
        console.error("Error saving template to Firestore:", error);
        alert("Template saved locally but could not sync to cloud. Please try again.");
        // Still update local state as fallback
        setAdminSelectedTemplate(selectedTemplate);
        navigate(selectedTemplate);
      }
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 150 * 1024) {
        alert('Logo image size must be below 150KB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof setInvoiceLogo === 'function') setInvoiceLogo(e.target.result);
        if (typeof setInvoiceBanner === 'function') setInvoiceBanner('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 150 * 1024) {
        alert('Banner image size must be below 150KB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof setInvoiceBanner === 'function') setInvoiceBanner(e.target.result);
        if (typeof setInvoiceLogo === 'function') setInvoiceLogo('');
      };
      reader.readAsDataURL(file);
    }
  };

  React.useEffect(() => {
    if (isAdmin && !adminSelectedTemplate) {
      alert('No invoice template selected. Please select a template.');
      navigate('/invoice-templates');
    }
  }, [isAdmin, adminSelectedTemplate, navigate]);

  // Fetch current invoice counter from Firestore (invoices/invoicecount)
  useEffect(() => {
    let mounted = true;
    const fetchCounter = async () => {
      setCounterLoading(true);
      try {
        const ref = doc(db, 'invoices', 'invoicecount');
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          setCounterValue(typeof data.invoicenumber === 'number' ? data.invoicenumber : 0);
        } else {
          setCounterValue(0);
        }
      } catch (err) {
        console.error('Error fetching invoice counter:', err);
        setCounterValue(null);
      } finally {
        setCounterLoading(false);
      }
    };
    fetchCounter();
    return () => { mounted = false; };
  }, []);

  if (!isAdmin && !adminSelectedTemplate) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5, textAlign: "center" }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>Invoice Template Not Selected</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No invoice template has been selected for your company yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please contact your administrator to configure and select an invoice template so you can proceed.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!isAdmin && adminSelectedTemplate) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5, textAlign: "center" }}>
        <Typography>
          Redirecting to your company's selected template...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configure Invoice Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          These settings will apply to all new invoices created.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Branding & Details</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can upload either a logo or a banner. Uploading one will remove the other.
            </Typography>

            <Input type="file" inputProps={{ accept: 'image/png, image/jpeg' }} onChange={handleLogoUpload} style={{ display: 'none' }} id="logo-upload" />
            <label htmlFor="logo-upload">
              <Button variant="contained" component="span" sx={{mr: 2, mb: 1}}>Upload Logo</Button>
            </label>

            <Input type="file" inputProps={{ accept: 'image/png, image/jpeg' }} onChange={handleBannerUpload} style={{ display: 'none' }} id="banner-upload" />
            <label htmlFor="banner-upload">
              <Button variant="contained" component="span" sx={{mb: 1}}>Upload Banner</Button>
            </label>
            <FormHelperText>Recommended banner size: 1600x400 pixels. Max 150KB.</FormHelperText>

            {invoiceLogo && (
              <Box mt={2} p={1} border="1px dashed grey" borderRadius={2} sx={{display: 'inline-block', position: 'relative'}}>
                <IconButton
                  size="small"
                  onClick={() => setInvoiceLogo('')}
                  sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <img src={invoiceLogo} alt="Uploaded logo" style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', display: 'block', padding: '8px' }} />
              </Box>
            )}

            {invoiceBanner && (
              <Box mt={2} p={1} border="1px dashed grey" borderRadius={2} sx={{position: 'relative'}}>
                <IconButton
                  size="small"
                  onClick={() => setInvoiceBanner('')}
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" display="block" sx={{ ml: 1, mb: 1, color: 'text.secondary' }}>Banner Preview:</Typography>
                <Box sx={{ width: '100%', height: '150px', overflow: 'hidden', borderRadius: 1 }}>
                  <img src={invoiceBanner} alt="Uploaded banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              </Box>
            )}
             {invoiceBanner && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          A banner has been uploaded — Business Name and Business Address are optional.
                                        </Typography>
                                      )}
            <Box mt={2}>
              <Typography variant="subtitle2">Numbering Preview (Tax Invoices)</Typography>
              {invoiceCustomCounter && (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Format: <strong>{(invoiceIdPrefix || 'PREX') + '-' + String(parseInt(invoiceCustomCounter) || 0).padStart(parseInt(invoiceCustomCounter).toString().length, '0')}</strong> up to <strong>{(invoiceIdPrefix || 'PREX') + '-' + String(Math.pow(10, parseInt(invoiceCustomCounter).toString().length) - 1).padStart(parseInt(invoiceCustomCounter).toString().length, '0')}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Proforma invoices: <strong>Proforma-{String(parseInt(invoiceCustomCounter) || 0).padStart(parseInt(invoiceCustomCounter).toString().length, '0')}</strong> to <strong>Proforma-{String(Math.pow(10, parseInt(invoiceCustomCounter).toString().length) - 1).padStart(parseInt(invoiceCustomCounter).toString().length, '0')}</strong>
                  </Typography>
                </>
              )}
              {!invoiceCustomCounter && (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Format: <strong>{(invoiceIdPrefix || 'PREX') + '-00000'}</strong> up to <strong>{(invoiceIdPrefix || 'PREX') + '-99999'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Proforma invoices: <strong>Proforma-00000</strong> to <strong>Proforma-99999</strong>
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Current next number (shared counter): {counterLoading ? 'Loading...' : (counterValue === null ? 'Unavailable' : `${String((counterValue || 0) + 1).padStart(invoiceCustomCounter ? parseInt(invoiceCustomCounter).toString().length : 5, '0')}`)}
              </Typography>
            </Box>

            <Box mt={3}>
              <Typography variant="subtitle1">Tax Invoice ID Prefix (Admin)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This 4-character alphanumeric prefix will appear only on tax invoices. It can only be changed by an admin and only once per year after being set. Proforma invoices always use the format Proforma-##### and share the same numeric counter.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Prefix"
                  value={newPrefix}
                  onChange={(e) => setNewPrefix(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0,4).toUpperCase())}
                  size="small"
                  sx={{ maxWidth: 140 }}
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const now = new Date();
                      setInvoiceIdPrefix(newPrefix);
                      setInvoicePrefixSetAt(now.toISOString());
                      alert('Invoice ID prefix has been set. It will be visible to users immediately.');
                    }}
                    disabled={!isAdmin || (typeof newPrefix !== 'string') || newPrefix.length !== 4 || (invoicePrefixSetAt && (new Date() - new Date(invoicePrefixSetAt) < 365 * 24 * 60 * 60 * 1000))}
                  >
                    Set Prefix
                  </Button>
                  &nbsp;
                  <Button variant="outlined" onClick={() => setNewPrefix('')}>Clear</Button>
                </Box>
              </Box>
              {invoiceIdPrefix && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Current prefix: <strong>{invoiceIdPrefix}</strong>
                  {invoicePrefixSetAt ? ` — Set on ${formatDateDDMMYYYY(invoicePrefixSetAt)}` : ''}
                </Typography>
              )}
              {invoicePrefixSetAt && (new Date() - new Date(invoicePrefixSetAt) < 365 * 24 * 60 * 60 * 1000) && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  Prefix is locked for one year after set. You can change it after {formatDateDDMMYYYY(new Date(new Date(invoicePrefixSetAt).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString())}.
                </Typography>
              )}

              <Box mt={3}>
                <Typography variant="subtitle2">Set Custom Counter ID Number</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Set the starting number for invoice counters. Examples: 123456, 98765432, 1010101
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Custom Counter Number"
                    type="number"
                    value={newCustomCounter}
                    onChange={(e) => setNewCustomCounter(e.target.value)}
                    placeholder="e.g., 123456"
                    size="small"
                    sx={{ maxWidth: 200 }}
                    inputProps={{ min: 0 }}
                    disabled={invoiceCounterSetAt && (new Date() - new Date(invoiceCounterSetAt) < 365 * 24 * 60 * 60 * 1000)}
                  />
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const now = new Date();
                        const nextNum = parseInt(newCustomCounter) + 1;
                        const digitLength = newCustomCounter.length;
                        const nextFormatted = String(nextNum).padStart(digitLength, '0');
                        setInvoiceCustomCounter(newCustomCounter);
                        setInvoiceCounterSetAt(now.toISOString());
                        setNewCustomCounter('');
                        alert(`Invoice counter has been set to ${newCustomCounter}. Next invoice will be numbered ${nextFormatted}.`);
                      }}
                      disabled={!isAdmin || newCustomCounter === '' || (invoiceCounterSetAt && (new Date() - new Date(invoiceCounterSetAt) < 365 * 24 * 60 * 60 * 1000))}
                    >
                      Set Counter
                    </Button>
                    &nbsp;
                    <Button variant="outlined" onClick={() => setNewCustomCounter('')} disabled={invoiceCounterSetAt && (new Date() - new Date(invoiceCounterSetAt) < 365 * 24 * 60 * 60 * 1000)}>Clear</Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Current counter starts at: <strong>{invoiceCustomCounter || '0'}</strong>
                  {invoiceCounterSetAt ? ` — Set on ${formatDateDDMMYYYY(invoiceCounterSetAt)}` : ''}
                </Typography>
                {invoiceCounterSetAt && (new Date() - new Date(invoiceCounterSetAt) < 365 * 24 * 60 * 60 * 1000) && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    Counter is locked for one year after set. You can change it after {formatDateDDMMYYYY(new Date(new Date(invoiceCounterSetAt).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString())}.
                  </Typography>
                )}
              </Box>

            </Box>
          </Box>

          <TextField
            label="Business Name"
            value={invoiceBusinessName || ""}
            onChange={(e) => setInvoiceBusinessName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Business Address"
            value={invoiceBusinessAddress || ""}
            onChange={(e) => setInvoiceBusinessAddress(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <Divider sx={{ my: 1 }} />
          <Typography variant="h6" gutterBottom>Policies & Defaults</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={showInvoiceGST}
                onChange={(e) => {
                  setShowInvoiceGST(e.target.checked);
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4caf50',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                  '& .MuiSwitch-switchBase': {
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#f44336',
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Apply GST/Tax in Invoices
                </Typography>
              </Box>
            }
            sx={{ mb: 3, alignItems: 'flex-start' }}
          />

          <FormControl fullWidth>
            <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary', fontSize: '1rem', fontWeight: 400 }}>
              Default Currency
            </Typography>
            <Select
              options={currencyOptions}
              value={currencyOptions.find(option => option.value === (invoiceCurrency?.code || 'INR'))}
              onChange={handleCurrencyChange}
              placeholder="Search and select a currency..."
              isSearchable={true}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '56px',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                }),
                container: (base) => ({
                  ...base,
                  fontFamily: 'Roboto, sans-serif'
                }),
              }}
            />
            <FormHelperText>Currency used for all new invoices</FormHelperText>
          </FormControl>

          <TextField
            label="Default Additional Notes"
            value={invoiceDefaultAdditionalNotes || ""}
            onChange={(e) => {
              if (typeof setInvoiceDefaultAdditionalNotes === 'function') {
                setInvoiceDefaultAdditionalNotes(e.target.value);
              }
            }}
            fullWidth
            multiline
            rows={4}
            helperText="These notes will appear on new invoices by default."
          />
          
          <TextField
            label="Default Terms and Conditions"
            value={invoiceDefaultTermsAndConditions || ""}
            onChange={(e) => {
              if (typeof setInvoiceDefaultTermsAndConditions === 'function') {
                setInvoiceDefaultTermsAndConditions(e.target.value);
              }
            }}
            fullWidth
            multiline
            rows={4}
            helperText="These terms will appear on new invoices by default."
          />
          
          <Divider sx={{ my: 1 }} />
          <Typography variant="h6" gutterBottom>Default Authorized Signature</Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Signature must be <b>JPEG, PNG, or JPG</b> and <b>between 30KB and 150KB</b>.
            </Typography>
          </Box>
          <Box>
            <Input 
              type="file" 
              inputProps={{ accept: 'image/jpeg, image/png, image/jpg' }} 
              onChange={handleDefaultSignatureUpload} 
              style={{ display: 'none' }} 
              id="default-signature-upload" 
            />
            <label htmlFor="default-signature-upload">
              <Button variant="contained" component="span" sx={{mr: 2, mb: 1}}>
                Upload Signature
              </Button>
            </label>
            {invoiceDefaultAuthorizedSignature && (
              <Box mt={2} p={2} border="1px dashed grey" borderRadius={2} sx={{display: 'inline-block', position: 'relative'}}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (typeof setInvoiceDefaultAuthorizedSignature === 'function') {
                      setInvoiceDefaultAuthorizedSignature('');
                    }
                    if (typeof setInvoiceDefaultAuthorizedSignatureName === 'function') {
                      setInvoiceDefaultAuthorizedSignatureName('');
                    }
                  }}
                  sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <img 
                  src={invoiceDefaultAuthorizedSignature} 
                  alt="Default authorized signature" 
                  style={{ maxWidth: '300px', maxHeight: '150px', objectFit: 'contain', display: 'block' }} 
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="h6" gutterBottom>Footer (will appear at the bottom of invoice)</Typography>
          <TextField
            label="Footer (optional)"
            value={invoiceFooter || ""}
            onChange={isAdmin ? (e) => { 
              if (typeof setInvoiceFooter === 'function') {
                setInvoiceFooter(e.target.value);
              }
            } : undefined}
            fullWidth
            multiline
            rows={2}
            helperText="This footer will be visible on all invoices. Leave blank to hide."
            sx={{ mb: 2 }}
            InputProps={{ readOnly: !isAdmin }}
          />
        </Box>
        
        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h5" component="h2" gutterBottom>Select Active Template</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button 
              variant={selectedTemplate === 'InTemplate' ? "contained" : "outlined"} 
              onClick={() => handleTemplateSelection('InTemplate')}
            >
              InTemplate
            </Button>
          </Box>
        </Box>
        {showPreview && selectedTemplate && (
          <Box mt={4}>
            <Typography variant="h6">Preview</Typography>
            <TemplatePreview template={selectedTemplate} onConfirm={handleConfirmTemplate} isAdmin={isAdmin} />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default InvoiceTemplates;